// lint-report-application-shell.ts -- deterministic report-app UX contract lint.
//
// This catches the failure mode where a validation harness or API-test surface is
// mistaken for a user-facing Cogo report application. It checks structure and
// semantics only; visual expression remains owned by injected design systems.

export type ReportApplicationShellSeverity = "error" | "warn" | "info"
export type ReportApplicationShellStatus = "pass" | "warn" | "blocked"

export type ReportApplicationShellFindingRule =
  | "missing-user-facing-report-application"
  | "harness-only-application"
  | "missing-global-command-header"
  | "missing-chat-command-input"
  | "missing-report-common-controls-in-header"
  | "common-controls-outside-global-header"
  | "missing-collapsible-sidebar"
  | "sidebar-not-full-height"
  | "missing-a4-report-canvas"
  | "missing-report-narrative"
  | "missing-report-chart-surface"
  | "missing-report-data-table"

export interface ReportApplicationShellFinding {
  rule: ReportApplicationShellFindingRule
  path: string
  severity: ReportApplicationShellSeverity
  message: string
}

export interface ReportApplicationShellSummary {
  nodeCount: number
  globalCommandHeaderCount: number
  chatCommandInputCount: number
  reportCommonControlsInHeaderCount: number
  collapsibleSidebarCount: number
  fullHeightSidebarCount: number
  a4PortraitCanvasCount: number
  reportNarrativeCount: number
  reportChartSurfaceCount: number
  reportDataTableCount: number
}

export interface ReportApplicationShellReport {
  schemaVersion: "cogo-report-application-shell-report.v1"
  status: ReportApplicationShellStatus
  findings: ReportApplicationShellFinding[]
  summary: ReportApplicationShellSummary
}

export interface ReportApplicationShellContract {
  schemaVersion: "cogo-report-application-shell-contract.v1"
  required: true
  appliesWhen: {
    applicationKind: "cogo.report-application"
  }
  validationMode: "strict-fail-closed"
  harnessBoundary: {
    userFacingApplicationRequired: true
    validationHarnessMustBeSeparateArtifact: true
  }
  globalChrome: {
    globalCommandHeaderRequired: true
    chatCommandInputRequired: true
    reportCommonControlsRequiredInHeader: true
  }
  navigation: {
    collapsibleSidebarRequired: true
    fullHeightSidebarRequired: true
  }
  reportCanvas: {
    pageFormat: "A4"
    orientation: "portrait"
    narrativeRegionRequired: true
    chartSurfaceRequired: true
    dataTableRequired: true
  }
  validator: {
    package: "@cogo/headless-core/lint-report-application-shell"
    function: "assessReportApplicationShell"
  }
}

type Node = Record<string, unknown>

interface NodeContext {
  node: Node
  path: string
  terms: Set<string>
  ancestors: NodeContext[]
}

export const REPORT_APPLICATION_SHELL_CONTRACT: ReportApplicationShellContract = {
  schemaVersion: "cogo-report-application-shell-contract.v1",
  required: true,
  appliesWhen: {
    applicationKind: "cogo.report-application",
  },
  validationMode: "strict-fail-closed",
  harnessBoundary: {
    userFacingApplicationRequired: true,
    validationHarnessMustBeSeparateArtifact: true,
  },
  globalChrome: {
    globalCommandHeaderRequired: true,
    chatCommandInputRequired: true,
    reportCommonControlsRequiredInHeader: true,
  },
  navigation: {
    collapsibleSidebarRequired: true,
    fullHeightSidebarRequired: true,
  },
  reportCanvas: {
    pageFormat: "A4",
    orientation: "portrait",
    narrativeRegionRequired: true,
    chartSurfaceRequired: true,
    dataTableRequired: true,
  },
  validator: {
    package: "@cogo/headless-core/lint-report-application-shell",
    function: "assessReportApplicationShell",
  },
}

const TERM_KEYS = new Set([
  "applicationKind",
  "blockRole",
  "componentRole",
  "designRole",
  "format",
  "id",
  "kind",
  "label",
  "layoutRole",
  "name",
  "orientation",
  "pageFormat",
  "paperSize",
  "region",
  "role",
  "schemaVersion",
  "semanticRole",
  "slot",
  "styleRole",
  "surfaceRole",
  "targetAudience",
  "title",
  "type",
])

const asObj = (value: unknown): Node | null =>
  value && typeof value === "object" && !Array.isArray(value) ? value as Node : null

const normalizeTerm = (value: unknown): string =>
  String(value)
    .trim()
    .toLowerCase()
    .replace(/[_\s]+/g, "-")

const pathSegment = (key: string, value: Node): string => {
  const id = typeof value.id === "string" && value.id ? `#${value.id}` : key
  return id.replace(/\s+/g, "-")
}

function collectTerms(node: Node): Set<string> {
  const terms = new Set<string>()
  for (const [key, value] of Object.entries(node)) {
    if (typeof value === "string" && TERM_KEYS.has(key)) {
      for (const part of value.split(/[^A-Za-z0-9._:-]+/)) {
        if (part) terms.add(normalizeTerm(part))
      }
      terms.add(normalizeTerm(value))
    }
  }

  const dataAttributes = asObj(node.dataAttributes)
  if (dataAttributes) {
    for (const [key, value] of Object.entries(dataAttributes)) {
      terms.add(normalizeTerm(key))
      if (typeof value === "string") terms.add(normalizeTerm(value))
    }
  }

  const design = asObj(node.design)
  if (design) {
    for (const [key, value] of Object.entries(design)) {
      terms.add(normalizeTerm(key))
      if (typeof value === "string") terms.add(normalizeTerm(value))
    }
  }

  return terms
}

function collectContexts(value: unknown): NodeContext[] {
  const contexts: NodeContext[] = []
  const seen = new Set<object>()

  const visit = (current: unknown, path: string, ancestors: NodeContext[]): void => {
    if (Array.isArray(current)) {
      current.forEach((item, index) => visit(item, `${path}[${index}]`, ancestors))
      return
    }

    const node = asObj(current)
    if (!node || seen.has(node)) return
    seen.add(node)

    const context: NodeContext = { node, path, terms: collectTerms(node), ancestors }
    contexts.push(context)

    for (const [key, child] of Object.entries(node)) {
      if (Array.isArray(child)) {
        child.forEach((item, index) => visit(item, `${path}.${key}[${index}]`, [...ancestors, context]))
      } else if (asObj(child)) {
        visit(child, `${path}.${pathSegment(key, child as Node)}`, [...ancestors, context])
      }
    }
  }

  visit(value, "$", [])
  return contexts
}

function hasTerm(context: NodeContext, needle: string): boolean {
  const normalized = normalizeTerm(needle)
  for (const term of context.terms) {
    if (term === normalized || term.includes(normalized)) return true
  }
  return false
}

function hasAnyTerm(context: NodeContext, needles: string[]): boolean {
  return needles.some((needle) => hasTerm(context, needle))
}

function hasStringValue(node: Node, keys: string[], expected: string): boolean {
  const normalizedExpected = normalizeTerm(expected)
  return keys.some((key) => typeof node[key] === "string" && normalizeTerm(node[key]) === normalizedExpected)
}

function hasTruthy(node: Node, keys: string[]): boolean {
  return keys.some((key) => node[key] === true || node[key] === "true")
}

function descendantsOf(contexts: NodeContext[], root: NodeContext): NodeContext[] {
  return contexts.filter((context) => context === root || context.ancestors.includes(root))
}

function isGlobalCommandHeader(context: NodeContext): boolean {
  return hasAnyTerm(context, [
    "global-command-header",
    "app-command-header",
    "global-header",
    "command-header",
  ])
}

function isChatCommandInput(context: NodeContext): boolean {
  const commandLike = hasAnyTerm(context, [
    "webmcp",
    "chat",
    "command-input",
    "command-prompt",
    "ask-cogo",
  ])
  const inputLike = hasAnyTerm(context, ["input", "textarea", "search", "prompt"])
    || hasStringValue(context.node, ["type"], "input")
    || hasStringValue(context.node, ["type"], "textarea")
  return commandLike && inputLike
}

function isReportCommonControl(context: NodeContext): boolean {
  return hasAnyTerm(context, [
    "report-common-controls",
    "report-controls",
    "period-filter",
    "date-range-filter",
    "frequency-toggle",
    "report-mode-selector",
  ])
}

function isSidebar(context: NodeContext): boolean {
  return hasAnyTerm(context, ["sidebar", "left-rail", "navigation-rail", "report-nav"])
}

function isCollapsibleSidebar(context: NodeContext): boolean {
  return isSidebar(context)
    && (
      hasTruthy(context.node, ["collapsible", "resizable"])
      || hasAnyTerm(context, ["collapsible", "collapse", "collapsed", "resizable", "resize-handle", "sidebar-state"])
    )
}

function isFullHeightSidebar(context: NodeContext): boolean {
  return isSidebar(context)
    && (
      hasTruthy(context.node, ["fullHeight"])
      || hasAnyTerm(context, [
        "full-height",
        "fullheight",
        "viewport-height",
        "min-h-screen",
        "h-screen",
        "stretch-to-bottom",
      ])
    )
}

function isA4PortraitCanvas(context: NodeContext): boolean {
  const a4 = hasAnyTerm(context, ["a4", "a4-report-canvas", "a4-page"])
    || hasStringValue(context.node, ["pageFormat", "paperSize", "format"], "A4")
  const portrait = hasAnyTerm(context, ["portrait", "a4-portrait"])
    || hasStringValue(context.node, ["orientation"], "portrait")
  const reportCanvas = hasAnyTerm(context, ["report-canvas", "report-page", "a4-report-canvas"])
  return a4 && portrait && reportCanvas
}

function isNarrative(context: NodeContext): boolean {
  return hasAnyTerm(context, [
    "report-narrative",
    "narrative",
    "insight",
    "analysis",
    "commentary",
    "executive-summary",
    "findings",
  ])
}

function isChartSurface(context: NodeContext): boolean {
  return hasAnyTerm(context, ["chart", "chart-surface", "combo-chart", "mixed-bar-line"])
}

function isDataTable(context: NodeContext): boolean {
  return hasAnyTerm(context, ["data-table", "datatable", "table", "report-table"])
}

function applicationTerms(contexts: NodeContext[]): Set<string> {
  const terms = new Set<string>()
  for (const context of contexts) {
    for (const term of context.terms) terms.add(term)
  }
  return terms
}

function addFinding(
  findings: ReportApplicationShellFinding[],
  rule: ReportApplicationShellFindingRule,
  message: string,
  path = "$",
): void {
  findings.push({ rule, path, severity: "error", message })
}

export function lintReportApplicationShell(app: unknown): ReportApplicationShellFinding[] {
  return assessReportApplicationShell(app).findings
}

export function assessReportApplicationShell(app: unknown): ReportApplicationShellReport {
  const contexts = collectContexts(app)
  const terms = applicationTerms(contexts)
  const findings: ReportApplicationShellFinding[] = []

  const reportApplication = terms.has("cogo.report-application")
    || terms.has("cogo-report-application")
    || terms.has("report-application")
  const userFacing = terms.has("user-facing") || terms.has("userfacing")
  const harnessOnly = terms.has("harness-only")
    || terms.has("validation-only")
    || terms.has("api-test")
    || terms.has("test-harness")
    || contexts.some((context) => context.node.isValidationHarness === true)

  if (!reportApplication || !userFacing) {
    addFinding(
      findings,
      "missing-user-facing-report-application",
      "A cogo.report-application must be marked as a user-facing application, not only as a fixture or harness.",
    )
  }
  if (harnessOnly) {
    addFinding(
      findings,
      "harness-only-application",
      "Validation harnesses must be separate artifacts; the product report application must be user-facing.",
    )
  }

  const headers = contexts.filter(isGlobalCommandHeader)
  const headerSubtree = headers.flatMap((header) => descendantsOf(contexts, header))
  const chatInputs = headerSubtree.filter(isChatCommandInput)
  const headerControls = headerSubtree.filter(isReportCommonControl)
  const controlsOutsideHeader = contexts.filter((context) =>
    isReportCommonControl(context) && !headerSubtree.includes(context)
  )
  const sidebars = contexts.filter(isSidebar)
  const collapsibleSidebars = sidebars.filter(isCollapsibleSidebar)
  const fullHeightSidebars = sidebars.filter(isFullHeightSidebar)
  const canvases = contexts.filter(isA4PortraitCanvas)
  const canvasSubtree = canvases.flatMap((canvas) => descendantsOf(contexts, canvas))
  const narrative = canvasSubtree.filter(isNarrative)
  const chartSurfaces = canvasSubtree.filter(isChartSurface)
  const dataTables = canvasSubtree.filter(isDataTable)

  if (headers.length === 0) {
    addFinding(findings, "missing-global-command-header", "Report apps must expose a global command header.")
  }
  if (chatInputs.length === 0) {
    addFinding(findings, "missing-chat-command-input", "The global command header must contain the WebMCP/chat command input.")
  }
  if (headerControls.length === 0) {
    addFinding(
      findings,
      "missing-report-common-controls-in-header",
      "Report-wide controls such as period/frequency/date range must live in the global command header.",
    )
  }
  if (controlsOutsideHeader.length > 0) {
    addFinding(
      findings,
      "common-controls-outside-global-header",
      "Report-wide controls were found outside the global command header.",
      controlsOutsideHeader[0].path,
    )
  }
  if (collapsibleSidebars.length === 0) {
    addFinding(findings, "missing-collapsible-sidebar", "Report apps must provide a collapsible/resizable left navigation rail.")
  }
  if (sidebars.length > 0 && fullHeightSidebars.length === 0) {
    addFinding(findings, "sidebar-not-full-height", "The left report navigation rail must extend to the bottom of the app shell.")
  }
  if (canvases.length === 0) {
    addFinding(findings, "missing-a4-report-canvas", "The report stage must include an A4 portrait report canvas.")
  }
  if (narrative.length === 0) {
    addFinding(findings, "missing-report-narrative", "The A4 report canvas must contain narrative/insight copy, not only charts and API test output.")
  }
  if (chartSurfaces.length === 0) {
    addFinding(findings, "missing-report-chart-surface", "The A4 report canvas must contain at least one chart surface.")
  }
  if (dataTables.length === 0) {
    addFinding(findings, "missing-report-data-table", "The A4 report canvas must contain at least one source-backed data table.")
  }

  const summary: ReportApplicationShellSummary = {
    nodeCount: contexts.length,
    globalCommandHeaderCount: headers.length,
    chatCommandInputCount: chatInputs.length,
    reportCommonControlsInHeaderCount: headerControls.length,
    collapsibleSidebarCount: collapsibleSidebars.length,
    fullHeightSidebarCount: fullHeightSidebars.length,
    a4PortraitCanvasCount: canvases.length,
    reportNarrativeCount: narrative.length,
    reportChartSurfaceCount: chartSurfaces.length,
    reportDataTableCount: dataTables.length,
  }

  return {
    schemaVersion: "cogo-report-application-shell-report.v1",
    status: findings.length > 0 ? "blocked" : "pass",
    findings,
    summary,
  }
}
